import React from "react";
import dayjs from "dayjs";
import "dayjs/locale/ar-sa";
import updateLocale from "dayjs/plugin/updateLocale";
import hijriMoment from "moment-hijri";

import messages from "../assets/locale/messages";
import store from "../store";
import { showHideSnackbar } from "../store/Snackbar/actions";
import Tooltip from "../components/Tooltip";
import { rolePermissions } from "./Permissions";
import { SA_UTC_OFFSET_MINUTES, eventStatuses, eventTypes } from "./Constants";
import {
  EVENT_VIEW_DEFAULT_TAB,
  EVENT_VIEW_TABS,
  getEventViewTabLabels,
} from "./eventViewTabs";
import { PAYROLL_URL_PARAMS } from "../components/Events/Event/Payroll/GeneratePayroll/constants/payrollUrlParams";
import isBetween from "dayjs/plugin/isBetween";
import { directories, uploadToS3 } from "./S3";

dayjs.extend(isBetween);
dayjs.extend(updateLocale);
// dayjs ar-sa ships meridiem as `hour > 12`, so noon (12) is labeled AM (ص).
dayjs.updateLocale("ar-sa", {
  meridiem: (hour) => (hour >= 12 ? "م" : "ص"),
});

const hijriMonths = [
  "Muharram",
  "Safar",
  "Rabia-Awwal",
  "Rabia-Thani",
  "Jumaada-Awal",
  "Jumaada-Thani",
  "Rajab",
  "Sha'ban",
  "Ramadan",
  "Shawwal",
  "Dhul-Qi'dah",
  "Dhul-Hijjah",
];

export const showSuccessMsg = (msg, showMsgDirectly, title) => {
  const lang = store.getState().locale.lang;
  const { successMsgs } = messages[lang];
  const message = showMsgDirectly ? msg : successMsgs[msg];
  if ((message == null || String(message).trim() === "") && !title) {
    return;
  }
  store.dispatch(
    showHideSnackbar({
      isOpen: true,
      type: "success",
      message,
      title,
    })
  );
};

export const showErrorMsg = (message, title) => {
  store.dispatch(
    showHideSnackbar({
      isOpen: true,
      type: "error",
      message,
      title,
    })
  );
};

export const showWarningMsg = (message) => {
  store.dispatch(
    showHideSnackbar({
      isOpen: true,
      type: "warning",
      message,
    })
  );
};

export const truncateByWords = (text = "", wordLimit = 3) => {
  const value = String(text ?? "").trim();
  if (!value) {
    return { text: value, truncated: false, full: value };
  }

  const words = value.split(/\s+/).filter(Boolean);
  if (words.length <= wordLimit) {
    return { text: value, truncated: false, full: value };
  }

  return {
    text: `${words.slice(0, wordLimit).join(" ")}...`,
    truncated: true,
    full: value,
  };
};

export const renderWordEllipsis = (text = "", wordLimit = 3) => {
  const { text: displayText, truncated, full } = truncateByWords(text, wordLimit);

  if (!displayText) return displayText;

  if (truncated) {
    return <Tooltip title={full} content={<span>{displayText}</span>} />;
  }

  return displayText;
};

export const renderTextEllipsis = (text = "", length = 30) => {
  if (text?.length > length) {
    const trimmedText = text.slice(0, length);
    return <Tooltip title={text} content={<span>{trimmedText}...</span>} />;
  } else {
    return text;
  }
};

export const formatDate = (date, format = "DD-MM-YYYY") =>
  dayjs(date).format(format);

/**
 * CLDR plural category for `count`. Arabic has six categories (zero/one/two/
 * few/many/other) with a real grammatical boundary at 11 — e.g. "10 أدوار"
 * (few) vs "11 دور" (many) — English only distinguishes one/other.
 */
export const getPluralCategory = (count, lang = "en") => {
  const n = Math.abs(Number(count) || 0);

  if (lang === "ar") {
    if (n === 0) return "zero";
    if (n === 1) return "one";
    if (n === 2) return "two";
    const mod100 = n % 100;
    if (mod100 >= 3 && mod100 <= 10) return "few";
    if (mod100 >= 11 && mod100 <= 99) return "many";
    return "other";
  }

  return n === 1 ? "one" : "other";
};

/**
 * Picks the plural-category template for `count` and interpolates it.
 * `labels` is `{ one: "{count} Role", other: "{count} Roles", ... }` — missing
 * categories (e.g. Arabic's `two`/`few`/`many` when only `one`/`other` are
 * defined) fall back to `other`.
 */
export const formatPluralLabel = (count, labels, lang = "en") => {
  const numeric = Number(count) || 0;
  const category = getPluralCategory(numeric, lang);
  const template = labels[category] || labels.other;
  return template.replace("{count}", String(numeric));
};

const WORKING_HOURS_DURATION_PATTERN =
  /^(\d+)\s*(?:H|ساعة)\s*(\d+)\s*(?:Min|دقيقة)$/i;

/** Parse "12 H 45 Min" (or localized equivalent) into hours and minutes. */
export const parseWorkingHoursDuration = (value) => {
  if (value == null || value === "") return null;

  if (typeof value === "object") {
    const hours = Number(value.hours ?? value.hour);
    const minutes = Number(value.minutes ?? value.minute);

    if (Number.isFinite(hours) || Number.isFinite(minutes)) {
      return {
        hours: Number.isFinite(hours) ? hours : 0,
        minutes: Number.isFinite(minutes) ? minutes : 0,
      };
    }

    return null;
  }

  const text = String(value).trim();
  if (!text) return null;

  const durationMatch = text.match(WORKING_HOURS_DURATION_PATTERN);
  if (durationMatch) {
    return {
      hours: Number(durationMatch[1]),
      minutes: Number(durationMatch[2]),
    };
  }

  const hoursOnlyMatch = text.match(
    /^(\d+(?:[.,]\d+)?)\s*(?:hr|hrs|hour|hours|h|ساعة)?\.?\s*$/i,
  );
  if (hoursOnlyMatch) {
    return { hours: Number(hoursOnlyMatch[1]), minutes: 0 };
  }

  return null;
};

/** Format hours/minutes duration for display (e.g. "12 H 45 Min" / "12 ساعة 45 دقيقة"). */
export const formatWorkingHoursDuration = (value, lang = "en") => {
  const parsed = parseWorkingHoursDuration(value);
  if (!parsed) {
    const fallback = String(value ?? "").trim();
    return fallback || "-";
  }

  const { shared } = messages[lang] || messages.en;
  const { hours, minutes } = parsed;
  const hourLabel = shared.workingDurationHour;
  const minuteLabel = shared.workingDurationMinute;

  if (minutes > 0) {
    return `${hours} ${hourLabel} ${minutes} ${minuteLabel}`;
  }

  return `${hours} ${hourLabel}`;
};

export const getLocalizedRoleName = (role = {}, lang = "en") => {
  const node = role?.role ?? role;
  const attrs = node?.attributes ?? node;
  const arabicName = attrs.name_ar;
  const englishName = attrs.name_en || attrs.name || attrs.role_name || "";

  if (lang === "ar" && arabicName?.trim()) {
    return arabicName.trim();
  }

  return englishName || "";
};

/** Minutes from UTC for the user's current timezone (any location). */
export const getLocalOffsetMinutes = () => -new Date().getTimezoneOffset();

export const addZeroToSingleNumber = (number) => {
  return String(number).padStart(2, "0");
};

export const getNamesInitials = (fullName) => {
  return fullName
    ? fullName
        .split(" ")
        .filter((_, i) => i < 2)
        .map((name) => name[0]?.toUpperCase())
        .join("")
    : "";
};

export const getFileName = (fileName) => {
  // let startIndex = fileName.indexOf("/files/") + "/files/".length;
  // return fileName.slice(startIndex);
  return fileName.replace(/^.*[\\/]/, "");
};

export const convertHtmlToText = (htmlString) =>
  htmlString ? htmlString.replace(/<[^>]+>/g, "") : htmlString;

export const textContainOnlySpaces = (text) =>
  text.length && /^\s*$/.test(text);

export const nonEmptyObject = (obj) => {
  let copyObj = { ...obj };
  for (const key in copyObj) {
    if (copyObj[key] === undefined || copyObj[key] === "") {
      copyObj[key] = null;
    }
  }
  return JSON.parse(JSON.stringify(copyObj));
};

export const generateUniqueSrc = (src) => {
  const timestamp = new Date().getTime();
  return `${src}?v=${timestamp}`;
};

/** Formats API datetime (SA +03:00) for display. Arabic: day-month-year (DD-M-YYYY), English: DD-MM-YYYY */
export const formatDateTime = (dateTime, lang = "en") => {
  const adjusted = dayjs(dateTime).add(
    SA_UTC_OFFSET_MINUTES - getLocalOffsetMinutes(),
    "minute"
  );
  const locale = lang === "ar" ? "ar-sa" : "en";
  const format = lang === "ar" ? "YYYY/MM/DD hh:mm a" : "DD-MM-YYYY hh:mm a";
  return adjusted.locale(locale).format(format);
};

// TODO: remove this temp fix afer dayjs issue is solved
export const checkFor12amIssue = (time, lang = "en") => {
  const hour11 = dayjs("11:59", "HH:mm");
  const hour13 = dayjs("13:00", "HH:mm");

  const timeObj = dayjs(time);

  const locale = lang === "ar" ? "ar-sa" : "en";

  let formattedTime = dayjs(time)
    .locale(locale)
    .format("hh:mm a");

  // Fix 12 PM Arabic issue only
  if (lang === "ar" && timeObj.isBetween(hour11, hour13, null, "[]")) {
    return formattedTime.replace("ص", "م");
  }

  return formattedTime;
};

/**
 * Parses a Hijri display string ("DD MonthName. YYYY", e.g. "09 Rabia-Awwal. 1420" —
 * the format the backend stores `hijri_date_of_birth` in, always in English) into a
 * `moment-hijri` instance, or `null` if it doesn't match that shape.
 */
const parseHijriDisplayDate = (_date) => {
  if (typeof _date !== "string" || _date.trim() === "") {
    return null;
  }
  const [dayRaw, monthRaw, yearRaw] = _date.trim().split(" ");
  const monthName = monthRaw?.replace(".", "").toLowerCase().trim();
  const monthIndex = hijriMonths.findIndex((m) => m.toLowerCase() === monthName);
  const day = parseInt(dayRaw, 10);
  const year = parseInt(yearRaw, 10);
  if (monthIndex === -1 || !Number.isFinite(day) || !Number.isFinite(year)) {
    return null;
  }
  // `.locale("en")` avoids moment-hijri's global side effect of defaulting the shared
  // moment locale to "ar-sa" (which formats output with Arabic-Indic digits).
  const hijri = hijriMoment().locale("en").iYear(year).iMonth(monthIndex).iDate(day);
  return hijri.isValid() ? hijri : null;
};

/**
 * Converts a Hijri display string ("DD MonthName. YYYY", e.g. "09 Rabia-Awwal. 1420")
 * to a Gregorian ISO date ("YYYY-MM-DD"). Uses `moment-hijri`'s real Hijri↔Gregorian
 * conversion — plain `moment` has no notion of the Hijri calendar.
 */
export const convertHijriToGregorian = (_date) => {
  const hijri = parseHijriDisplayDate(_date);
  return hijri ? hijri.format("YYYY-MM-DD") : undefined;
};

/**
 * Converts a Gregorian ISO date ("YYYY-MM-DD") to a Hijri display string
 * ("DD MonthName. YYYY", e.g. "09 Rabia-Awwal. 1420") — the inverse of
 * `convertHijriToGregorian`, using the same real Hijri conversion.
 */
export const convertGregorianToHijri = (_date) => {
  if (typeof _date !== "string" || _date.trim() === "") {
    return undefined;
  }
  const parsed = hijriMoment(_date, "YYYY-MM-DD", true).locale("en");
  if (!parsed.isValid()) return undefined;
  return `${String(parsed.iDate()).padStart(2, "0")} ${hijriMonths[parsed.iMonth()]}. ${parsed.iYear()}`;
};

/**
 * Re-renders a stored Hijri display string ("DD MonthName. YYYY") with the month name
 * localized for the given UI language — the backend always stores it in English, so an
 * Arabic UI otherwise shows an English month name (e.g. "25 Safar. 1430" instead of
 * "٢٥ صفر ١٤٣٠"). Falls back to the raw value unchanged if it doesn't parse.
 */
export const formatHijriDisplayDate = (_date, lang) => {
  const hijri = parseHijriDisplayDate(_date);
  if (!hijri) return _date;
  // No trailing period after the month name — matches the plain "22 February 2009"
  // style already used for the Gregorian date display.
  return hijri.locale(lang === "ar" ? "ar-sa" : "en").format("iD iMMMM iYYYY");
};

export const isNumericValue = (value) =>
  !isNaN(value) && !isNaN(parseFloat(value));

// FOR IMAGE CONVERT TO BASE64 PPT FILE APPLICANTS
export const fetchImageAsBase64 = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const fetchImageBuffer = async (imageUrl) => {
  const response = await fetch(imageUrl);
  const blob = await response.blob();
  const arrayBuffer = await blob.arrayBuffer();
  return new Uint8Array(arrayBuffer);
};

export const getLinkName = (linkName) => {
  switch (linkName) {
    case "الفعاليات":
      return "Events";
    case "الالتحاق بالفعاليات":
      return "Joining Events";
    case "الترقية":
      return "Promotions";
    case "الطلبات":
      return "Requests";
    case "المتقدمين":
      return "Applicants";
    case "ساعات العمل":
      return "Timesheet";
    case "التقييمات":
      return "Evaluations";
    case "الموظفين":
      return "Employees";
    case "الرواتب":
      return "Payroll";
    case "التقارير":
      return "Reports";
    case "إدارة الأدوار":
      return "Roles Management";
    case "العملاء":
      return "Clients";
    case "الشركات":
      return "Companies";
    case "طلبات التسجيل":
      return "Registration Req.";
    case "إدارة العملاء":
      return "Manage Clients";
    case "إدارة التقارير":
      return "Manage Reports";
    case "درجة الاستبيان":
      return "Survey Score";
    case "الخريطة الحرارية":
      return "Heatmap";
    default: {
      return linkName;
    }
  }
};

export const getRolesActions = (name) =>
  rolePermissions()?.permissions?.find(
    (permission) => permission.module_name === name
  );

export const getRolesActionPath = (name) =>
  rolePermissions()?.permissions?.find(
    (permission) => permission.permission_url === name
  ) || {};

export const getIndexPermissions = (arr, path) =>
  arr.find((p) => p.module_name === path);

export const workHelperShift = (shift) => {
  return shift
    ?.split("_")
    ?.map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
};

export const localizationKey = (key) => {
  switch (key) {
    case "Check-in Status":
      return "checkInStatus";
    case "Check-out Time":
      return "checkOutTime";
    case "Check-in Time":
      return "checkInTime";
    case "Daily Amount":
      return "dailyAmount";
    case "Date":
      return "date";
    case "Email":
      return "email";
    case "ID":
      return "id";
    case "Image URL":
      return "imageUrl";
    case "Name":
      return "name";
    case "Number of Working Days":
      return "numberOfWorkingDays";
    case "Role":
      return "role";
    case "Status":
      return "status";
    case "Stc Number":
      return "stcNumber";
    case "National Id":
      return "nationalId";
    case "Supervisor":
      return "supervisor";
    case "Total Amount":
      return "totalAmount";
    case "Zone":
      return "zone";
    case "Gender":
      return "gender";
    case "Note":
      return "note";
    case "Phone Number":
      return "phoneNumber";
    case "Number of Training Days":
      return "numberOfTrainingDays";
    case "Training Daily Amount":
    case "Daily Training Amount":
      return "trainingDailyAmount";
    case "Training Total Amount":
    case "Total Training Amount":
      return "trainingTotalAmount";
    case "Total Shift Amount":
    case "totalShiftAmount":
      return "totalShiftAmount";
    case "total_shift_amount":
      return "totalShiftAmount";
    case "Auto Deduction Amount":
    case "auto_deduction_amount":
      return "autoDeductionAmount";
    case "Manual Deduction Amount":
    case "manual_deduction_amount":
      return "manualDeductionAmount";
    case "Manual Deduction Reason":
    case "manual_deduction_reason":
      return "manualDeductionReason";
    case "Reward Amount":
    case "reward_amount":
      return "rewardAmount";
    case "Reward Reason":
    case "reward_reason":
      return "rewardReason";
    case "numberOfTrainingDays":
    case "number_of_training_days":
      return "numberOfTrainingDays";
    case "trainingDailyAmount":
    case "daily_training_amount":
      return "trainingDailyAmount";
    case "trainingTotalAmount":
    case "total_training_amount":
      return "trainingTotalAmount";
    case "totalAmount":
    case "total_amount":
      return "totalAmount";
    default:
      return "";
  }
};

export const localizedGender = (gender) => {
  switch (gender) {
    case "male":
      return "ذَكَر";
    case "female":
      return "أُنثَى";
    default:
      return "";
  }
};

export const localizedCheckInStatus = (checkStatus) => {
  switch (checkStatus) {
    case "Not checked-in":
      return "لم يتم تسجيل الوصول";
    case "Checked-in":
      return "تم تسجيل الوصول";
    default:
      return "";
  }
};
export const localizedStatus = (status) => {
  switch (status) {
    case "Assigned":
      return "مُكلَّف";
    case "Unassigned":
      return "غير مخصص";
    default:
      return "";
  }
};

/**
 * @param {string|number|boolean|null|undefined|Record<string, boolean>|Array} input
 * @returns {string[]}
 */
const clxParts = (input) => {
  if (input == null || input === false) {
    return [];
  }
  if (typeof input === "string" || typeof input === "number") {
    const s = String(input).trim();
    return s ? [s] : [];
  }
  if (Array.isArray(input)) {
    const out = [];
    for (let i = 0; i < input.length; i += 1) {
      out.push(...clxParts(input[i]));
    }
    return out;
  }
  if (typeof input === "object") {
    const out = [];
    const keys = Object.keys(input);
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      if (input[key]) {
        out.push(key);
      }
    }
    return out;
  }
  return [];
};

/**
 * Combines class names: strings, conditional strings, arrays, and flag objects.
 * @param {...(string|number|undefined|null|false|Record<string, boolean>|Array)} args
 * @returns {string}
 *
 * @example
 * clx("btn", isActive && "btn--active")  // "btn btn--active"
 * clx("banner", { "banner--open": isOpen })  // "banner banner--open"
 */
export const clx = (...args) => {
  const out = [];
  for (let i = 0; i < args.length; i += 1) {
    out.push(...clxParts(args[i]));
  }
  return out.join(" ");
};

/**
 * Localized event status for tabs / breadcrumb / chips (matches listEvents.eventTabs).
 * @param {string | null | undefined} status
 * @param {{ ongoing: string, upcoming: string, previous: string, pending: string, cancelled: string }} eventTabs
 * @returns {string}
 */
export const formatEventStatusTabLabel = (status, eventTabs) => {
  if (status == null || String(status).trim() === "") {
    return "";
  }
  const s = String(status).toLowerCase();
  if (s === eventStatuses.past || s === "past") {
    return eventTabs.previous;
  }
  if (
    s === "cancelled" ||
    s === "canceled" ||
    s === String(eventStatuses.cancelled).toLowerCase()
  ) {
    return eventTabs.cancelled;
  }
  if (eventTabs[s]) {
    return eventTabs[s];
  }
  return String(status);
};

/**
 * Builds a pathname + `tab` query string. Clears timesheet-only params when leaving timesheet.
 */
export const buildPathWithTab = (pathname, tab, existingSearch = "") => {
  const params = new URLSearchParams(existingSearch);
  params.set("tab", tab);
  if (tab !== "timesheet") {
    params.delete("applicant");
  }
  if (tab !== EVENT_VIEW_TABS.payroll) {
    params.delete(PAYROLL_URL_PARAMS.generate);
  }
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
};

/**
 * When `activeTab` differs from `defaultTab`, links the last crumb to the default tab
 * and appends the active tab as the current page.
 */
export const appendActiveUrlTabBreadcrumb = ({
  breadcrumbs,
  activeTab,
  defaultTab,
  tabLabelsByValue,
  viewPathname,
  search = "",
}) => {
  if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
    return breadcrumbs;
  }

  const normalizedActive = activeTab || defaultTab;
  const result = breadcrumbs.map((item) => ({ ...item }));

  if (normalizedActive === defaultTab) {
    result[result.length - 1] = {
      ...result[result.length - 1],
      path: "",
    };
    return result;
  }

  const activeLabel = tabLabelsByValue?.[normalizedActive];
  if (!activeLabel) {
    return result;
  }

  result[result.length - 1] = {
    ...result[result.length - 1],
    path: viewPathname
      ? buildPathWithTab(viewPathname, defaultTab, search)
      : "",
  };

  result.push({ path: "", label: activeLabel });
  return result;
};

/**
 * Links the last crumb to `listPath` and appends a non-linked detail label (e.g. applicant name).
 */
export const appendNestedDetailBreadcrumb = ({
  breadcrumbs,
  detailLabel,
  listPath,
}) => {
  if (!detailLabel || !Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
    return breadcrumbs;
  }

  const result = breadcrumbs.map((item) => ({ ...item }));
  result[result.length - 1] = {
    ...result[result.length - 1],
    path: listPath || "",
  };
  result.push({ path: "", label: detailLabel });
  return result;
};

/** Timesheet tab list view — same event path, no selected applicant. */
export const buildTimesheetApplicantsListPath = (pathname, search = "") => {
  const params = new URLSearchParams(search);
  params.set("tab", "timesheet");
  params.delete("applicant");
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
};

/** Event applicants tab list view (e.g. breadcrumb back from applicant profile). */
export const buildEventApplicantsListPath = (pathname, search = "") =>
  buildPathWithTab(pathname, EVENT_VIEW_TABS.applicants, search);

/**
 * Applicant profile opened from event applicants tab:
 * Events → status → event → Applicants → applicant name.
 */
export const buildFromEventApplicantProfileBreadcrumbs = ({
  fromEvent,
  eventsListPath,
  eventsMessages,
  applicantName,
  eventViewPath,
}) => {
  if (!fromEvent?.eventId) {
    return [];
  }

  const breadcrumbs = [
    {
      path: eventsListPath,
      label: eventsMessages.listEvents.listEventsBreadcrumb,
    },
  ];

  if (fromEvent.eventStatus) {
    breadcrumbs.push({
      path: `${eventsListPath}?tab=${fromEvent.eventStatus}`,
      label: formatEventStatusTabLabel(
        fromEvent.eventStatus,
        eventsMessages.listEvents?.eventTabs ?? {},
      ),
    });
  }

  breadcrumbs.push({
    path: "",
    label: fromEvent.eventName || "",
  });

  let list = appendActiveUrlTabBreadcrumb({
    breadcrumbs,
    activeTab: EVENT_VIEW_TABS.applicants,
    defaultTab: EVENT_VIEW_DEFAULT_TAB,
    tabLabelsByValue: getEventViewTabLabels(eventsMessages.viewEvents),
    viewPathname: eventViewPath,
    search: "",
  });

  if (applicantName) {
    list = appendNestedDetailBreadcrumb({
      breadcrumbs: list,
      detailLabel: applicantName,
      listPath: buildEventApplicantsListPath(eventViewPath),
    });
  }

  return list;
};

/**
 * Localized payment period (daily / monthly).
 * @param {string | null | undefined} period
 * @param {{ daily: string, monthly: string }} listEvents messages.events.listEvents
 * @returns {string}
 */
export const formatEventPaymentPeriod = (period, listEvents) => {
  if (period == null || String(period).trim() === "") {
    return "";
  }
  const p = String(period).toLowerCase();
  if (p === eventTypes.daily) {
    return listEvents.daily;
  }
  if (p === eventTypes.monthly) {
    return listEvents.monthly;
  }
  return String(period);
};

/**
 * Ensures a phone dial code is in the canonical `+DDD` shape expected by the
 * backend. Accepts values like `"966"`, `"+966"`, `" +966 "`, or empty/nullish
 * (falls back to the Saudi default). Strips any stray leading `+` characters
 * so we never produce `"++966"`.
 *
 * @param {string | null | undefined} raw
 * @param {string} [fallback="+966"] dial code to use when `raw` is empty.
 * @returns {string}
 *
 * @example
 * normalizeDialCode("966")   // "+966"
 * normalizeDialCode("+20")   // "+20"
 * normalizeDialCode("")      // "+966"
 */
export const normalizeDialCode = (raw, fallback = "+966") => {
  const value = String(raw ?? "").trim() || fallback;
  return value.startsWith("+") ? value : `+${value.replace(/^\++/, "")}`;
};

/**
 * Compose a display mobile number from dial code + national number.
 * e.g. dialCode "+20" + "1001234567" → "+20 1001234567"
 * Returns the bare phone number when no dial code is present.
 */
export const formatMobileNumber = (phoneNumber, dialCode) => {
  const phone = (phoneNumber || "").trim();
  if (!phone) return "";
  const code = String(dialCode ?? "")
    .trim()
    .replace(/^\++/, "");
  return code ? `+${code} ${phone}` : phone;
};

export const uploadFileWithProgress = async (file, key, prefix, setUploadState) => {
  if (file === null) return null;
  if (!(file instanceof File)) return undefined;

  const ext = file.name.split(".").pop() || "pdf";
  const fileName = `${prefix}.${ext}`;

  setUploadState({ target: key, percent: 0 });

  const uploaded = await uploadToS3(file, fileName, directories.companyRegistration, {
    onProgress: (percent) => setUploadState({ target: key, percent }),
  });

  return uploaded?.link ?? null;
};
